import { AuthEnums } from "models/enums";
import { PersonSalaryFormTypes, SalaryDraftFormTypes, SalaryFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { store } from "store";
import { IconDefinitionConstants } from "utils/constants";
import useAPIClientHelper from "./useAPIClientHelper";
import { DateHelper, EnumHelper, FileHelper, LocalizationHelperInstance } from "utils/helpers";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";

function useSalaryService() {
  const { apiClientInstance } = useAPIClientHelper();

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useSalaryPermissions)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const isAutoCreated = salaryService.isAutoCreatedSimpleSalary();
   *
   * @example
   * // New way (recommended):
   * function useSalaryPermissions() {
   *   const company = useSelector((state: AppState) => state.auth.selectedCompany);
   *
   *   const isAutoCreatedSimpleSalary = () => {
   *     return (
   *       company.superkollSalaryType === ApiModels.SuperkollSalaryType.Simple &&
   *       company.createSalaryAutoFromSalaryPersonEntry
   *     );
   *   };
   *
   *   return { isAutoCreatedSimpleSalary };
   * }
   *
   * // In component:
   * const { isAutoCreatedSimpleSalary } = useSalaryPermissions();
   * const isAutoCreated = isAutoCreatedSimpleSalary();
   */
  const isAutoCreatedSimpleSalary = () => {
    const company = store.getState().auth.selectedCompany;
    return (
      company.superkollSalaryType === ApiModels.SuperkollSalaryType.Simple &&
      company.createSalaryAutoFromSalaryPersonEntry
    );
  };
  const acceptSalaryDraft = (salaryDraftId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SalaryDraftsClient.Accept(salaryDraftId);
  };

  const rejectSalaryDraft = (salaryDraftId: number, message?: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SalaryDraftsClient.Reject(salaryDraftId, { message: message } as ApiModels.SalaryDraftRejectAction)
      .then((res) => res.data);
  };
  const assignSalaryDraft = (salaryDraftId: number, data: ApiModels.SalaryDraftAssignAction) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SalaryDraftsClient.Assign(salaryDraftId, data)
      .then((res) => res.data);
  };

  const downloadSalaryDraft = (salaryDraftId: number) => {
    apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    })
      .SalaryDraftsClient.DownloadAttachment(salaryDraftId)
      .then((res) => {
        FileHelper.download(res);
      });
  };
  const getSalaryDraftAttachment = (salaryDraftId: number, attachmentId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SalaryDraftsClient.GetAttachment(salaryDraftId, attachmentId)
      .then((res) => res.data);
  };

  const getSalaryDraftAttachmentData = (salaryDraftId: number, attachmentId: number | string) => {
    return getSalaryDraftAttachment(salaryDraftId, attachmentId as number).then((res) => res.data ?? "");
  };

  const filterSalaryDrafts = (baseFilter: ApiModels.SalaryDraftFilter, filter: SalaryDraftFormTypes.SalaryFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SalaryDraftsClient.GetSalaryDrafts({
      ...baseFilter,
      ...salaryDraftReportToAPIFilter(filter),
    });
  };

  const salaryDraftReportToAPIFilter = (
    formFilter?: SalaryDraftFormTypes.SalaryFilter
  ): Omit<ApiModels.SalaryDraftFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      from: formFilter?.yearMonth?.start ? DateHelper.getYearMonth(formFilter.yearMonth.start) : undefined,
      to: formFilter?.yearMonth?.end ? DateHelper.getYearMonth(formFilter.yearMonth.end) : undefined,
      states: formFilter?.state?.map((s) => s.id as ApiModels.SalaryDraftState) ?? getValidSalaryDraftStates(),
    };
  };
  const getValidSalaryDraftStates = () => {
    const statesToExclude = [ApiModels.SalaryDraftState.NotSent, ApiModels.SalaryDraftState.Removed];
    return EnumHelper.getNumericItemValues(ApiModels.SalaryDraftState).filter((s) => !statesToExclude.includes(s));
  };

  const getSalaryDraftStateText = (state: ApiModels.SalaryDraftState) => {
    return LocalizationHelperInstance.getFormattedMessage(`Salary.SalaryDraftState_${state}`);
  };

  const searchSalaryAdmins = (searchString: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SalaryDraftsClient.SearchSalaryAdmins({
        searchStr: searchString,
        includeSelf: false,
      })
      .then((res) =>
        res.data.map(
          (employee): AutocompleteListItem => ({
            id: employee.id,
            value: employee.name ?? "",
            icon: IconDefinitionConstants.WUser,
          })
        )
      );
  };

  const approvePersonSalary = (selected: ApiModels.PersonSalaryEntryApprovalItem[]) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).PersonSalaryClient.Approve({
      entriesToApprove: selected.map((e) => ({
        id: e.id,
        amount: Number(e.amount),
      })),
    });
  };

  const getPersonSalaryReport = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .PersonSalaryClient.GetPersonSalaryReport()
      .then((res) => res.data);
  };

  const getUpdatedRow = (salaryId: number, personId: number, value: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .PersonSalaryClient.GetUpdatedPersonSalaryRow(salaryId, { personId: personId, monthlySalary: value })
      .then((res) => res.data);
  };

  const filterPersonSalaries = (baseFilter: WintUtilsWebApi.Filter, filter: PersonSalaryFormTypes.Filter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).PersonSalaryClient.GetSalaryEntries({
      ...baseFilter,
      ...personSalaryReportToAPIFilter(filter),
    });
  };

  const personSalaryReportToAPIFilter = (
    formFilter?: PersonSalaryFormTypes.Filter
  ): Omit<ApiModels.PersonSalaryEntryFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      personIds: formFilter?.personId ? [formFilter.personId] : undefined,
      yearAndMonthFrom: formFilter?.yearMonth?.start ? DateHelper.getYearMonth(formFilter.yearMonth.start) : undefined,
      yearAndMonthTo: formFilter?.yearMonth?.end ? DateHelper.getYearMonth(formFilter.yearMonth.end) : undefined,
      payoutDateFrom: formFilter?.paymentDate?.start,
      payoutDateTo: formFilter?.paymentDate?.end,
      states: formFilter?.state?.map((s) => s.id as ApiModels.PersonSalaryViewState),
      taxAndFeesFrom: formFilter?.taxesAndFees?.lowest,
      taxAndFeesTo: formFilter?.taxesAndFees?.highest,
      benefitsFrom: formFilter?.benefits?.lowest,
      benefitsTo: formFilter?.benefits?.highest,
      grossAmountFrom: formFilter?.grossSalary?.lowest,
      grossAmountTo: formFilter?.grossSalary?.highest,
      isActive: formFilter?.isActive !== undefined ? !!formFilter?.isActive : undefined,
    };
  };

  const downloadSalarySpecification = (salaryId: number) => {
    apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    })
      .PersonSalaryClient.DownloadSalarySpecificationDocument(salaryId)
      .then((res) => {
        FileHelper.download(res);
      });
  };

  const getPersonSalaryStateText = (status: ApiModels.PersonSalaryViewState) => {
    return LocalizationHelperInstance.getFormattedMessage(`Salary.PersonSalaryViewState_${status}`);
  };

  const updateEmployeeSalarySettings = (
    employeeId: number,
    salarySettings: ApiModels.EmployeeSalarySettingPut,
    hideErrorsForCodes?: string[]
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true, hideErrorsForCodes })
      .SettingsClient.UpdateSalary(employeeId, salarySettings)
      .then((res) => res.data);
  };

  const getEmployeesSalarySettings = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SettingsClient.EmployeeSettings()
      .then((res) => res.data);
  };

  const updateEmployeesSalarySettings = (settings: ApiModels.EmployeesSalarySettings) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .SettingsClient.UpdateSalaries(settings)
      .then((res) => res.data);
  };

  const grossSalaryReportFilter = (
    baseFilter: WintUtilsWebApi.Filter,
    formFilter?: SalaryFormTypes.GrossSalaryFilter
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SalaryClient.FilterGrossSalaries({
      ...baseFilter,
      year: formFilter?.year ?? DateHelper.getYear(DateHelper.today()),
    });
  };

  const addExtraSalary = (salary: ApiModels.PersonSalaryExtraEntryPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).PersonSalaryClient.PostExtraSalary(
      salary
    );
  };

  const searchSalaryEmployees = (searchString: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .PersonSalaryClient.GetSearchEmployeeByName(searchString)
      .then((res) => res.data);
  };

  const predictExtraSalaryInfo = (grossAmount: number, employeeId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).PersonSalaryClient.PredictExtraSalaryInfo(grossAmount, employeeId, true);
  };

  const getExtraSalaryInfo = (salary: SalaryFormTypes.AddExtraSalary) => {
    return predictExtraSalaryInfo(salary.amount, salary.employee.id as number);
  };

  return {
    isAutoCreatedSimpleSalary,
    acceptSalaryDraft,
    rejectSalaryDraft,
    assignSalaryDraft,
    downloadSalaryDraft,
    getSalaryDraftAttachment,
    getSalaryDraftAttachmentData,
    filterSalaryDrafts,
    salaryDraftReportToAPIFilter,
    getValidSalaryDraftStates,
    getSalaryDraftStateText,
    searchSalaryAdmins,
    approvePersonSalary,
    getPersonSalaryReport,
    getUpdatedRow,
    filterPersonSalaries,
    downloadSalarySpecification,
    getPersonSalaryStateText,
    updateEmployeeSalarySettings,
    getEmployeesSalarySettings,
    personSalaryReportToAPIFilter,
    updateEmployeesSalarySettings,
    grossSalaryReportFilter,
    addExtraSalary,
    searchSalaryEmployees,
    getExtraSalaryInfo,
  };
}

export default useSalaryService;
